import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, Clock, Bell, Check, Coffee, UtensilsCrossed, Receipt, 
  ArrowRight, Grid3X3, List, Maximize2, RefreshCw, SplitSquareHorizontal,
  MoreVertical, ArrowLeftRight, CreditCard, Printer, WifiOff, Server
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import localSync from '../../utils/localSync';

// Table status configuration
const STATUS_CONFIG = {
  empty: { color: 'bg-slate-100 text-slate-500 border-slate-200', label: 'Empty', gridColor: 'bg-slate-50 border-slate-200' },
  seated: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Seated', gridColor: 'bg-blue-50 border-blue-300' },
  ordering: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Ordering', gridColor: 'bg-amber-50 border-amber-300' },
  served: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Served', gridColor: 'bg-green-50 border-green-300' },
  bill_requested: { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Bill', gridColor: 'bg-purple-50 border-purple-300' },
  paying: { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Paying', gridColor: 'bg-orange-50 border-orange-300' },
};

export default function WaiterDashboard() {
  const { api, user } = useAuth();
  const [tables, setTables] = useState([]);
  const [allTables, setAllTables] = useState([]); // All restaurant tables for grid view
  const [pendingRequests, setPendingRequests] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Offline & Local Sync State
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [localSyncStatus, setLocalSyncStatus] = useState({
    isHost: false,
    connected: false,
    hostUrl: null
  });
  
  // Transfer items dialog
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferSource, setTransferSource] = useState(null);
  const [transferTarget, setTransferTarget] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Local Sync - Auto-connect to host when offline
  useEffect(() => {
    if (!isOnline && user?.restaurant_id) {
      // Try to discover and connect to main POS
      const connectToHost = async () => {
        try {
          const host = await localSync.discoverHost();
          if (host) {
            await localSync.connectToHost(host.url);
            toast.success('Connected to main POS for offline sync');
          } else {
            // Try manual connection
            const hostIP = localStorage.getItem('localSync_hostIP');
            if (hostIP) {
              await localSync.connectToHost(`ws://${hostIP}:9200`);
              toast.success('Connected to main POS');
            }
          }
        } catch (error) {
          console.log('Could not connect to local host:', error.message);
        }
      };
      
      connectToHost();
    }

    // Update status on changes
    const handleStatus = () => setLocalSyncStatus(localSync.getStatus());
    localSync.on('connected', handleStatus);
    localSync.on('disconnected', handleStatus);
    
    return () => {
      localSync.off('connected', handleStatus);
      localSync.off('disconnected', handleStatus);
    };
  }, [isOnline, user?.restaurant_id]);
  
  // Bill split dialog
  const [showBillSplitDialog, setShowBillSplitDialog] = useState(false);
  const [billSplitTable, setBillSplitTable] = useState(null);

  const fetchTables = useCallback(async () => {
    try {
      const response = await api.get('/waiter/my-tables');
      setTables(response.data.sessions || []);
      setPendingRequests(response.data.pending_requests || 0);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchAllTables = useCallback(async () => {
    try {
      // Get restaurant ID from user context or first session
      if (tables.length > 0) {
        const restaurantId = tables[0].restaurant_id;
        const response = await api.get(`/restaurants/${restaurantId}/tables`);
        setAllTables(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch all tables:', error);
    }
  }, [api, tables]);

  useEffect(() => {
    fetchTables();
    const interval = setInterval(fetchTables, 15000); // Refresh every 15 seconds
    return () => clearInterval(interval);
  }, [fetchTables]);

  useEffect(() => {
    if (tables.length > 0) {
      fetchAllTables();
    }
  }, [tables.length, fetchAllTables]);

  const assignToTable = async (sessionId) => {
    try {
      await api.post(`/waiter/assign-table/${sessionId}`);
      toast.success('Assigned to table');
      fetchTables();
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  const updateItemStatus = async (sessionId, itemId, status) => {
    try {
      await api.put(`/table-sessions/${sessionId}/items/${itemId}`, { status });
      toast.success(`Item marked as ${status}`);
      fetchTables();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const acknowledgeRequest = async (sessionId, requestId) => {
    try {
      await api.put(`/table-sessions/${sessionId}/waiter-requests/${requestId}`, { status: 'acknowledged' });
      toast.success('Request acknowledged');
      fetchTables();
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const handleTransferItems = async () => {
    if (!transferSource || !transferTarget || selectedItems.length === 0) {
      toast.error('Please select items and target table');
      return;
    }
    
    try {
      await api.post(`/table-sessions/${transferSource.id}/transfer-items`, {
        target_session_id: transferTarget,
        item_ids: selectedItems
      });
      toast.success(`${selectedItems.length} items transferred`);
      setShowTransferDialog(false);
      setTransferSource(null);
      setTransferTarget('');
      setSelectedItems([]);
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer items');
    }
  };

  const requestBill = async (sessionId) => {
    try {
      await api.post(`/table-sessions/${sessionId}/request-bill`);
      toast.success('Bill requested');
      fetchTables();
    } catch (error) {
      toast.error('Failed to request bill');
    }
  };

  const printBill = (table) => {
    // Create print-friendly bill
    const printWindow = window.open('', '_blank');
    const billHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill - Table ${table.table_number}</title>
        <style>
          body { font-family: 'Courier New', monospace; padding: 20px; max-width: 300px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; }
          .item { display: flex; justify-content: space-between; padding: 5px 0; }
          .total { border-top: 2px solid #000; margin-top: 10px; padding-top: 10px; font-weight: bold; }
          .footer { text-align: center; margin-top: 20px; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>Table ${table.table_number}</h2>
          <p>${new Date().toLocaleString()}</p>
        </div>
        ${table.items?.map(item => `
          <div class="item">
            <span>${item.quantity}x ${item.name}</span>
            <span>£${(item.total || 0).toFixed(2)}</span>
          </div>
        `).join('')}
        <div class="item total">
          <span>TOTAL</span>
          <span>£${(table.subtotal || 0).toFixed(2)}</span>
        </div>
        <div class="footer">Thank you for dining with us!</div>
      </body>
      </html>
    `;
    printWindow.document.write(billHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const getTableSession = (tableNumber) => {
    return tables.find(t => t.table_number === tableNumber);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="waiter-dashboard-loading">
        <div className="h-10 bg-slate-200 rounded w-48 animate-pulse"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isFullscreen ? 'p-6 bg-slate-100 min-h-screen' : ''}`} data-testid="waiter-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Table Overview</h1>
          <p className="text-slate-500 mt-1">Manage tables, orders, and requests</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Offline/Sync Indicator */}
          {!isOnline && (
            <div className="flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full">
              <WifiOff className="w-4 h-4" />
              <span className="font-semibold text-sm">Offline</span>
              {localSyncStatus.connected && (
                <Badge className="bg-blue-500 text-xs ml-1">
                  <Server className="w-3 h-3 mr-1" />
                  Local
                </Badge>
              )}
            </div>
          )}
          
          {pendingRequests > 0 && (
            <div className="flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full animate-pulse">
              <Bell className="w-5 h-5" />
              <span className="font-semibold">{pendingRequests} requests</span>
            </div>
          )}
          
          {/* View Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <Button 
              variant={viewMode === 'grid' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-btn"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('list')}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchTables} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          <Button variant="outline" size="sm" onClick={toggleFullscreen} data-testid="fullscreen-btn">
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-slate-700">{allTables.length || tables.length}</p>
            <p className="text-xs text-slate-500">Total Tables</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{tables.filter(t => t.status === 'seated').length}</p>
            <p className="text-xs text-blue-500">Seated</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{tables.filter(t => t.status === 'ordering').length}</p>
            <p className="text-xs text-amber-500">Ordering</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-purple-700">{tables.filter(t => t.status === 'bill_requested').length}</p>
            <p className="text-xs text-purple-500">Bills</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-green-700">
              {formatCurrency(tables.reduce((sum, t) => sum + (t.subtotal || 0), 0))}
            </p>
            <p className="text-xs text-green-500">Total Sales</p>
          </CardContent>
        </Card>
      </div>

      {/* Status Legend */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <div key={key} className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs ${config.color}`}>
            <div className={`w-3 h-3 rounded-full ${config.gridColor}`}></div>
            {config.label}
          </div>
        ))}
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList>
          <TabsTrigger value="active">Active Tables ({tables.length})</TabsTrigger>
          <TabsTrigger value="grid">Floor Plan</TabsTrigger>
        </TabsList>
        
        {/* Active Tables View */}
        <TabsContent value="active" className="mt-4">
          {tables.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Coffee className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No active tables</h3>
                <p className="text-slate-500">Tables will appear here when customers are seated</p>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {tables.map(table => {
                const status = STATUS_CONFIG[table.status] || STATUS_CONFIG.seated;
                const hasPendingRequests = table.waiter_requests?.some(r => r.status === 'pending');
                const pendingItems = table.items?.filter(i => i.status === 'pending').length || 0;
                
                return (
                  <Card 
                    key={table.id}
                    className={`cursor-pointer hover:shadow-lg transition-all border-2 ${status.gridColor} ${hasPendingRequests ? 'ring-2 ring-red-400 animate-pulse' : ''}`}
                    onClick={() => setSelectedTable(selectedTable?.id === table.id ? null : table)}
                    data-testid={`table-card-${table.table_number}`}
                  >
                    <CardContent className="pt-4">
                      {/* Table Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">Table {table.table_number}</h3>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                            <Users className="w-3 h-3" />
                            <span>{table.party_size}</span>
                            <Clock className="w-3 h-3 ml-1" />
                            <span>{formatTime(table.seated_at)}</span>
                          </div>
                        </div>
                        
                        {/* Quick Actions Menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={e => e.stopPropagation()}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setTransferSource(table); setShowTransferDialog(true); }}>
                              <ArrowLeftRight className="w-4 h-4 mr-2" /> Transfer Items
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setBillSplitTable(table); setShowBillSplitDialog(true); }}>
                              <SplitSquareHorizontal className="w-4 h-4 mr-2" /> Split Bill
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); printBill(table); }}>
                              <Printer className="w-4 h-4 mr-2" /> Print Bill
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); requestBill(table.id); }}>
                              <CreditCard className="w-4 h-4 mr-2" /> Request Bill
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      {/* Status Badge */}
                      <Badge className={`${status.color} mb-3`}>{status.label}</Badge>
                      
                      {/* Alerts */}
                      {(hasPendingRequests || pendingItems > 0) && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {hasPendingRequests && (
                            <Badge variant="destructive" className="text-xs">
                              <Bell className="w-3 h-3 mr-1" /> Request
                            </Badge>
                          )}
                          {pendingItems > 0 && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              {pendingItems} pending
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Order Summary */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                        <div>
                          <p className="text-xs text-slate-500">{table.items?.length || 0} items</p>
                          <p className="text-lg font-bold text-slate-900">{formatCurrency(table.subtotal)}</p>
                        </div>
                        
                        {!table.waiter_id ? (
                          <Button 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); assignToTable(table.id); }}
                            className="btn-primary text-xs"
                          >
                            Assign
                          </Button>
                        ) : table.waiter_id === user?.id ? (
                          <Badge className="bg-teal-100 text-teal-700 text-xs">Mine</Badge>
                        ) : null}
                      </div>
                    </CardContent>
                    
                    {/* Expanded Order Details */}
                    {selectedTable?.id === table.id && (
                      <div className="px-4 pb-4 border-t bg-white/50">
                        <h4 className="font-semibold text-slate-900 mt-3 mb-2 text-sm">Order Items</h4>
                        {table.items?.length === 0 ? (
                          <p className="text-xs text-slate-500">No items yet</p>
                        ) : (
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {table.items?.map(item => (
                              <div key={item.id} className="flex items-center justify-between bg-white p-2 rounded text-sm">
                                <div className="flex-1">
                                  <span className="font-medium">{item.quantity}x {item.name}</span>
                                  {item.notes && <span className="text-xs text-red-500 ml-2">{item.notes}</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-xs">{formatCurrency(item.total)}</span>
                                  {item.status === 'pending' && (
                                    <Button size="sm" variant="outline" className="h-6 text-xs"
                                      onClick={(e) => { e.stopPropagation(); updateItemStatus(table.id, item.id, 'preparing'); }}>
                                      Start
                                    </Button>
                                  )}
                                  {item.status === 'preparing' && (
                                    <Button size="sm" className="h-6 text-xs bg-green-500 hover:bg-green-600"
                                      onClick={(e) => { e.stopPropagation(); updateItemStatus(table.id, item.id, 'served'); }}>
                                      Serve
                                    </Button>
                                  )}
                                  {item.status === 'served' && (
                                    <Badge className="bg-green-100 text-green-700 text-xs">Done</Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Pending Requests */}
                        {table.waiter_requests?.filter(r => r.status === 'pending').length > 0 && (
                          <div className="mt-3">
                            <h4 className="font-semibold text-red-700 mb-2 text-sm">Pending Requests</h4>
                            {table.waiter_requests.filter(r => r.status === 'pending').map(req => (
                              <div key={req.id} className="flex items-center justify-between bg-red-50 p-2 rounded border border-red-200 mb-1">
                                <span className="text-sm text-red-700 capitalize">{req.type}</span>
                                <Button size="sm" className="h-6 text-xs"
                                  onClick={(e) => { e.stopPropagation(); acknowledgeRequest(table.id, req.id); }}>
                                  <Check className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          ) : (
            // List View
            <div className="space-y-2">
              {tables.map(table => {
                const status = STATUS_CONFIG[table.status] || STATUS_CONFIG.seated;
                const hasPendingRequests = table.waiter_requests?.some(r => r.status === 'pending');
                
                return (
                  <Card key={table.id} className={`${hasPendingRequests ? 'ring-2 ring-red-400' : ''}`}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold ${status.gridColor}`}>
                            {table.table_number}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">Table {table.table_number}</span>
                              <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
                              {hasPendingRequests && (
                                <Badge variant="destructive" className="text-xs animate-pulse">
                                  <Bell className="w-3 h-3" />
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                              <span><Users className="w-3 h-3 inline mr-1" />{table.party_size}</span>
                              <span><Clock className="w-3 h-3 inline mr-1" />{formatTime(table.seated_at)}</span>
                              <span>{table.items?.length || 0} items</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-bold">{formatCurrency(table.subtotal)}</span>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setSelectedTable(table)}>
                              View
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setTransferSource(table); setShowTransferDialog(true); }}>
                              <ArrowLeftRight className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => printBill(table)}>
                              <Printer className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
        
        {/* Floor Plan Grid View */}
        <TabsContent value="grid" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Floor Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {(allTables.length > 0 ? allTables : Array.from({ length: 12 }, (_, i) => ({ table_number: i + 1 }))).map(table => {
                  const session = getTableSession(table.table_number);
                  const status = session ? STATUS_CONFIG[session.status] || STATUS_CONFIG.seated : STATUS_CONFIG.empty;
                  const hasPendingRequests = session?.waiter_requests?.some(r => r.status === 'pending');
                  
                  return (
                    <div
                      key={table.id || table.table_number}
                      className={`relative aspect-square rounded-lg border-2 flex flex-col items-center justify-center cursor-pointer hover:scale-105 transition-all ${status.gridColor} ${hasPendingRequests ? 'ring-2 ring-red-400 animate-pulse' : ''}`}
                      onClick={() => session && setSelectedTable(session)}
                      data-testid={`floor-table-${table.table_number}`}
                    >
                      <span className="text-lg font-bold text-slate-700">{table.table_number}</span>
                      {session && (
                        <>
                          <span className="text-xs text-slate-500">{session.party_size} guests</span>
                          {session.subtotal > 0 && (
                            <span className="text-xs font-semibold text-slate-700">{formatCurrency(session.subtotal)}</span>
                          )}
                        </>
                      )}
                      {hasPendingRequests && (
                        <Bell className="absolute top-1 right-1 w-4 h-4 text-red-500" />
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Transfer Items Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Items</DialogTitle>
            <DialogDescription>
              Move items from Table {transferSource?.table_number} to another table
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Select Items */}
            <div>
              <label className="text-sm font-medium mb-2 block">Select Items to Transfer</label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {transferSource?.items?.map(item => (
                  <div key={item.id} className="flex items-center gap-3">
                    <Checkbox
                      id={item.id}
                      checked={selectedItems.includes(item.id)}
                      onCheckedChange={(checked) => {
                        setSelectedItems(prev => 
                          checked ? [...prev, item.id] : prev.filter(id => id !== item.id)
                        );
                      }}
                    />
                    <label htmlFor={item.id} className="flex-1 text-sm cursor-pointer">
                      {item.quantity}x {item.name} - {formatCurrency(item.total)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Target Table */}
            <div>
              <label className="text-sm font-medium mb-2 block">Transfer To</label>
              <Select value={transferTarget} onValueChange={setTransferTarget}>
                <SelectTrigger>
                  <SelectValue placeholder="Select target table" />
                </SelectTrigger>
                <SelectContent>
                  {tables.filter(t => t.id !== transferSource?.id).map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      Table {t.table_number} ({t.party_size} guests)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
            <Button onClick={handleTransferItems} disabled={selectedItems.length === 0 || !transferTarget}>
              Transfer {selectedItems.length} Items
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bill Split Dialog - Placeholder */}
      <Dialog open={showBillSplitDialog} onOpenChange={setShowBillSplitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Split Bill - Table {billSplitTable?.table_number}</DialogTitle>
            <DialogDescription>
              Total: {formatCurrency(billSplitTable?.subtotal)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-8 text-center text-slate-500">
            <SplitSquareHorizontal className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>Bill splitting is available in the POS system.</p>
            <p className="text-sm mt-2">Go to POS → Select order → Split Bill</p>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowBillSplitDialog(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Selected Table Detail Modal */}
      {selectedTable && viewMode === 'list' && (
        <Dialog open={!!selectedTable && viewMode === 'list'} onOpenChange={() => setSelectedTable(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Table {selectedTable.table_number}</DialogTitle>
              <DialogDescription>
                {selectedTable.party_size} guests • {formatTime(selectedTable.seated_at)}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Order Items</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedTable.items?.map(item => (
                    <div key={item.id} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                      <div>
                        <span className="font-medium">{item.quantity}x {item.name}</span>
                        {item.notes && <span className="text-xs text-red-500 block">{item.notes}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{formatCurrency(item.total)}</span>
                        {item.status === 'pending' && (
                          <Button size="sm" variant="outline"
                            onClick={() => updateItemStatus(selectedTable.id, item.id, 'preparing')}>
                            Start
                          </Button>
                        )}
                        {item.status === 'preparing' && (
                          <Button size="sm" className="bg-green-500 hover:bg-green-600"
                            onClick={() => updateItemStatus(selectedTable.id, item.id, 'served')}>
                            Served
                          </Button>
                        )}
                        {item.status === 'served' && (
                          <Badge className="bg-green-100 text-green-700">Done</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-lg font-bold">Total: {formatCurrency(selectedTable.subtotal)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => printBill(selectedTable)}>
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                  <Button onClick={() => requestBill(selectedTable.id)}>
                    <CreditCard className="w-4 h-4 mr-2" /> Request Bill
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
